// library imports
import {
  Button,
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  Popconfirm,
  Table,
  Typography,
} from "antd";
import { forwardRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { useBreakpoint } = Grid;

/** Cell that makes up table that can be edited */
export const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  fieldOptions, //props to get to input node
  fieldComponent, //specified field components, such as Select, TimePicker etc
  formItemProps, //can specify rule or other props for specified form item
  ...restProps
}) => {
  let inputNode = <Input />;
  switch (inputType) {
    case "number":
      inputNode = <InputNumber {...fieldOptions} />;
      break;
    case "date":
      inputNode = <DatePicker {...fieldOptions} />;
      break;
    default:
      inputNode = <Input {...fieldOptions} />;
      break;
  }

  if (fieldComponent) {
    inputNode = fieldComponent;
  }

  let itemProps = {
    name: dataIndex,
    style: {
      margin: 0,
    },
    rules: [
      {
        required: true,
        message: `Please Input ${title}!`,
      },
    ],
  };
  // If something is input
  if (formItemProps) {
    itemProps = formItemProps;
  }

  return (
    <td {...restProps}>
      {editing ? <Form.Item {...itemProps}>{inputNode}</Form.Item> : children}
    </td>
  );
};

/** Editable table based on Ant Design example */
const EditableTable = forwardRef((props, ref) => {
  const form = props.form;
  const [editingKey, setEditingKey] = useState("");
  const screens = useBreakpoint();

  const { onSave, onCancel } = props;
  const { dataSource, columns } = props;
  const { showAdd = false, showDelete, onDelete } = props; //can change showAdd to true if you decide to allow user to add new row
  const { formInsert } = props;
  const { breakpoint } = props;

  const isEditing = (record) => record.key === editingKey;

  const indexedData = [];
  // Add key based on array index
  for (let i = 0; i < dataSource.length; i++) {
    indexedData.push({
      ...dataSource[i],
      key: i,
    });
  }

  // If a new row is to be inserted, create temporary row
  if (editingKey === -1) {
    // Key is -1 for new row
    let emptyRow = {
      key: -1,
    };
    // Insert temporary data
    columns.forEach((column, index) => {
      if (column.default !== undefined) {
        emptyRow[column.dataIndex] = column.default;
      } else {
        switch (column.inputType) {
          case "text":
            emptyRow[column.dataIndex] = "";
            break;
          case "number":
            emptyRow[column.dataIndex] = 0;
            break;
          case "date":
            emptyRow[column.dataIndex] = dayjs().startOf("D");
            break;
          default:
            emptyRow[column.dataIndex] = null;
            break;
        }
      }
    });
    indexedData.unshift(emptyRow);
  }

  /** When edit is clicked */
  const edit = (record) => {
    // Custom conversion to form data
    if (formInsert) {
      form.setFieldsValue(formInsert(record));
    } else {
      form.setFieldsValue({
        ...record,
      });
    }
    setEditingKey(record.key);
  };

  /** When cancel is clicked */
  const cancel = () => {
    setEditingKey("");

    if (onCancel) {
      onCancel();
    }
  };

  /** When save is clicked */
  const save = async (key) => {
    try {
      const row = await form.validateFields();

      // Call the assigned callback function
      if (onSave) {
        onSave(key, row);
      }

      setEditingKey("");
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  /** When delete is clicked */
  const trigDelete = async (key) => {
    // Call the assigned callback function
    if (onDelete) {
      onDelete(key);
    }
  };

  /** When add new row is clicked */
  const onAddRow = () => {
    setEditingKey(-1);
  };

  // Add method that produces complete information for the cell render
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType ? col.inputType : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        fieldOptions: col.fieldOptions ? col.fieldOptions : {},
        fieldComponent: col.fieldComponent ? col.fieldComponent : null,
        formItemProps: col?.formItemProps ? col.formItemProps : null,
      }),
    };
  });

  // Temporarily comment out first
  // Add the column for Edit, Save, Cancel operations
  mergedColumns.push({
    title: "Operation",
    dataIndex: "operation",
    render: (_, record) => {
      const editable = isEditing(record);
      return editable ? (
        <span>
          <Typography.Link
            onClick={() => save(record.key)}
            style={{
              marginRight: 8,
            }}
          >
            Save
          </Typography.Link>
          <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
            <a>Cancel</a>
          </Popconfirm>
        </span>
      ) : (
        <span>
          <Typography.Link
            disabled={editingKey !== ""}
            onClick={() => edit(record)}
            style={{
              marginRight: 8,
            }}
          >
            Edit
          </Typography.Link>
          {showDelete === true && (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => trigDelete(record.key)}
            >
              <a>Delete</a>
            </Popconfirm>
          )}
        </span>
      );
    },
  });

  return (
    <div>
      {showAdd && (
        <div
          style={{
            width: "100%",
            padding: "8px",
            justifyContent: "flex-end",
            display: breakpoint
              ? screens[breakpoint]
                ? "flex"
                : "none"
              : "flex",
          }}
        >
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={onAddRow}
            disabled={editingKey !== ""}
          >
            Add New
          </Button>
        </div>
      )}
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          size="large"
          {...props}
          pagination={{
            onChange: cancel,
            pageSize: 5,
            ...props.pagination,
          }}
          dataSource={indexedData}
          columns={mergedColumns}
        />
      </Form>
    </div>
  );
});

export default EditableTable;
